import React from 'react';
import { Button } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faLock } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { channelAlertTone, type ChannelAlertTone } from '../orders/aggregator-order.utils';
import classes from './pos-top-bar.module.scss';

export type PosTabKey = 'areas' | 'orders' | 'limit-items';

const TAB_ALERT_CLASS: Record<ChannelAlertTone, string> = {
    blue: classes.tabChannelAlertBlue,
    green: classes.tabChannelAlertGreen,
    coral: classes.tabChannelAlertCoral,
};

export type PosTopBarTab = {
    key: PosTabKey;
    label: string;
};

export type PosTopBarProps = {
    tabs: PosTopBarTab[];
    activeTab: PosTabKey | null;
    tabBadges?: Partial<Record<PosTabKey, number>>;
    // Prefix of the newest channel order awaiting action here; picks the pulse colour.
    tabChannelAlerts?: Partial<Record<PosTabKey, string | null>>;
    onTabChange: (tab: PosTabKey) => void;
    // A pulsing tab means pending approvals, so tapping it jumps straight to the queue.
    onOpenApprovals?: () => void;
    employeeName: string;
    employeePhotoUrl?: string | null;
    onSwitchEmployee: () => void;
    onOpenSettings: () => void;
    canOpenSettings: boolean;
};

// Architectural summary: POS shell top bar.
// Function-library reference: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-postopbar
// underline marking the active screen; left side holds the operator cluster:
// settings hamburger (opens the SettingsHubModal in PosShellPage), lock icon to
// switch employee (clears the employee session via parent callback), and
// the employee chip with avatar + full name.
// When receiptMode is provided the tab list is replaced by three receipt-specific
// action sections (Print | Actions | Guest Name), matching the design reference.
// Touches:
//   - PosShellPage.tsx (renders this bar; provides tabs, settings open handler,
//     switch-employee handler).
//   - EmployeeSessionProvider via PosShellPage's `handleSwitchEmployee`.
// Breaking change risk: changing tab keys or callback shape silently breaks
// PosShellPage's tab-driven content rendering.
const PosTopBarComponent: React.FC<PosTopBarProps> = ({
    tabs,
    activeTab,
    tabBadges,
    tabChannelAlerts,
    onTabChange,
    onOpenApprovals,
    employeeName,
    employeePhotoUrl,
    onSwitchEmployee,
    onOpenSettings,
    canOpenSettings,
}) => {
    const initial = (employeeName.trim().charAt(0) || '?').toUpperCase();

    return (
        <div className={classes.bar} role="banner">
            <div className={classes.tabs} role="tablist">
                {tabs.map((tab) => {
                    const isActive = tab.key === activeTab;
                    const hasPendingApproval = (tabBadges?.[tab.key] ?? 0) > 0;
                    const channelAlert = channelAlertTone(tabChannelAlerts?.[tab.key]);
                    return (
                        <Button
                            key={tab.key}
                            unstyled
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            className={`${classes.tab} ${isActive ? classes.tabActive : ''} ${hasPendingApproval ? classes.tabPendingApproval : ''} ${channelAlert ? TAB_ALERT_CLASS[channelAlert] : ''}`}
                            data-testid={`topbar-tab-${tab.key}`}
                            onClick={() => (hasPendingApproval && onOpenApprovals ? onOpenApprovals() : onTabChange(tab.key))}
                        >
                            <span className={classes.tabLabel}>{tab.label}</span>
                        </Button>
                    );
                })}
            </div>

            <div className={classes.controls}>
                <Button
                    unstyled
                    type="button"
                    className={classes.iconButton}
                    aria-label="הגדרות"
                    data-testid="topbar-settings"
                    disabled={!canOpenSettings}
                    onClick={onOpenSettings}
                >
                    <FontAwesomeIcon icon={faBars as IconProp} />
                </Button>

                <Button
                    unstyled
                    type="button"
                    className={`${classes.iconButton} ${classes.desktopOnly}`}
                    onClick={onSwitchEmployee}
                    aria-label="החלף עובד"
                    title="החלף עובד"
                    data-testid="topbar-switch-employee"
                >
                    <FontAwesomeIcon icon={faLock as IconProp} />
                </Button>

                <div className={`${classes.employeeChip} ${classes.desktopOnly}`} data-testid="topbar-employee-chip">
                    <span className={classes.employeeName}>{employeeName}</span>
                    <span className={classes.employeeAvatar}>
                        {employeePhotoUrl ? (
                            <img src={employeePhotoUrl} alt={employeeName} />
                        ) : (
                            initial
                        )}
                    </span>
                </div>
            </div>
        </div>
    );
};

export const PosTopBar = React.memo(PosTopBarComponent);
